import React, { useEffect, useRef, useState } from 'react';
import './TeraChat.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const GREETING =
  "Hey, I'm Tera 👋 your AI career copilot. Ask me anything about Terview, interviews, resumes, or the job hunt.";

// Robot mascot image — drop the PNG at public/images/tera-bot.png. CRA's
// PUBLIC_URL resolves to '' in dev / the deployed base in prod.
const TERA_AVATAR = `${process.env.PUBLIC_URL || ''}/images/tera-bot.png`;

// Teaser messages that pop up above the FAB on first visit to draw the
// user into a conversation. They cycle one after another, then the final
// "need help?" line stays visible until dismissed or the chat is opened.
const TEASERS = [
  "Hey, how are you doing? 👋",
  "Are you ready to ace your next interview?",
  "Ask me anything about Terview.ai ✨",
  "What are you looking for today?",
  "Need help with anything? I'm here to chat.",
];
const TEASER_INITIAL_DELAY_MS = 3500;  // wait this long after mount
const TEASER_CYCLE_MS         = 4200;  // hold each line this long
// Bumped from `…dismissed` → `…dismissed.v2` so any sessionStorage flag
// stamped by the previous "dismiss on open" logic is ignored and users
// see the teasers on a fresh visit again.
const TEASER_DISMISS_KEY      = 'terview.tera.teaser.dismissed.v2';

/**
 * TeraChat — floating AI chat agent on the bottom-right of the website.
 *
 * - Closed state: a pulsing pink "T" FAB.
 * - Open state: a chat panel with streaming Tera replies, text input,
 *   and a voice button (records via MediaRecorder, transcribes via
 *   /api/tera/transcribe).
 *
 * Both Tera endpoints are public (no auth) so anonymous landing-page
 * visitors can talk to her too.
 */
export default function TeraChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { id: 'greet', role: 'tera', text: GREETING },
  ]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [error, setError] = useState('');

  // Teaser bubble — cycles through TEASERS while the panel stays closed.
  // -1 means "not yet shown / hidden"; otherwise it's the index into
  // TEASERS. Once the chat is opened (or × is clicked), we stamp
  // sessionStorage so the teaser doesn't re-show on subsequent page
  // navigations within the same tab session.
  const [teaserIndex, setTeaserIndex] = useState(-1);
  const [teaserDismissed, setTeaserDismissed] = useState(() => {
    try { return sessionStorage.getItem(TEASER_DISMISS_KEY) === '1'; }
    catch { return false; }
  });

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioStreamRef = useRef(null);

  // Auto-scroll the conversation when new tokens land.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isStreaming]);

  // Focus the textarea when the panel opens.
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 80);
  }, [open]);

  // Clean up any leftover mic stream on unmount.
  useEffect(() => () => {
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(t => t.stop());
    }
  }, []);

  // Teaser scheduling — fires the first message after a short delay, then
  // advances every TEASER_CYCLE_MS. Halts on the final teaser so the
  // "need help?" line stays pinned until the user clicks the FAB or ×.
  useEffect(() => {
    if (open || teaserDismissed) {
      setTeaserIndex(-1);
      return;
    }
    if (teaserIndex < 0) {
      const t = setTimeout(() => setTeaserIndex(0), TEASER_INITIAL_DELAY_MS);
      return () => clearTimeout(t);
    }
    if (teaserIndex >= TEASERS.length - 1) return;  // hold on the last line
    const t = setTimeout(() => setTeaserIndex(i => i + 1), TEASER_CYCLE_MS);
    return () => clearTimeout(t);
  }, [teaserIndex, open, teaserDismissed]);

  // (We used to dismiss the teaser the moment the chat opened, but that
  // killed it forever after the user peeked once. Now the teaser only
  // gets dismissed when the user clicks × on it OR sends a message —
  // see `send()` below. The cycle naturally re-pins on the final line
  // and resumes when the panel is closed, so it's never noisy.)

  const dismissTeaser = (e) => {
    if (e) e.stopPropagation();
    try { sessionStorage.setItem(TEASER_DISMISS_KEY, '1'); } catch { /* ignore */ }
    setTeaserDismissed(true);
    setTeaserIndex(-1);
  };

  const send = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || isStreaming) return;
    setInput('');
    setError('');

    // The user has engaged — silence the teaser for the rest of the
    // session so we don't keep nudging them mid-conversation.
    if (!teaserDismissed) {
      try { sessionStorage.setItem(TEASER_DISMISS_KEY, '1'); } catch { /* ignore */ }
      setTeaserDismissed(true);
    }

    // Build history payload for OpenAI shape (user / assistant turns).
    const history = messages
      .filter(m => !m.streaming && (m.role === 'user' || m.role === 'tera'))
      .slice(-10)
      .map(m => ({
        role: m.role === 'tera' ? 'assistant' : 'user',
        content: m.text,
      }));
    const apiMessages = [...history, { role: 'user', content: trimmed }];

    const placeholderId = `tera-${Date.now()}`;
    setMessages(prev => [
      ...prev,
      { id: `user-${Date.now()}`, role: 'user', text: trimmed },
      { id: placeholderId, role: 'tera', text: '', streaming: true },
    ]);
    setIsStreaming(true);

    try {
      const r = await fetch(`${API_BASE_URL}/api/tera/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: apiMessages }),
      });

      if (!r.ok || !r.body) {
        throw new Error("Tera couldn't respond — try again in a moment.");
      }

      const reader = r.body.getReader();
      const decoder = new TextDecoder();
      let fullText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        const lines = chunk.split('\n').filter(l => l.startsWith('data: '));
        for (const line of lines) {
          const data = line.replace('data: ', '');
          if (data === '[DONE]') continue;
          try {
            const parsed = JSON.parse(data);
            const token = parsed.choices?.[0]?.delta?.content || '';
            if (token) {
              fullText += token;
              setMessages(prev =>
                prev.map(m => (m.id === placeholderId ? { ...m, text: fullText } : m))
              );
            }
          } catch {
            /* ignore malformed SSE chunks */
          }
        }
      }

      setMessages(prev =>
        prev.map(m => (m.id === placeholderId ? { ...m, streaming: false } : m))
      );
    } catch (err) {
      setError(err.message || 'Tera is unavailable.');
      setMessages(prev =>
        prev.map(m =>
          m.id === placeholderId
            ? {
                ...m,
                text: "Sorry, I couldn't reach my brain just then. Please try again.",
                streaming: false,
                error: true,
              }
            : m
        )
      );
    } finally {
      setIsStreaming(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  /* ── Voice recording ─────────────────────────────────────────────── */

  const startRecording = async () => {
    setError('');
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Voice input is not supported in this browser.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';
      const mr = new MediaRecorder(stream, { mimeType: mime });
      mediaRecorderRef.current = mr;
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(t => t.stop());
        audioStreamRef.current = null;
        if (blob.size > 0) await transcribeAndSend(blob);
      };
      mr.start();
      setIsRecording(true);
    } catch {
      setError('Microphone access denied.');
    }
  };

  const stopRecording = () => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== 'inactive') mr.stop();
    setIsRecording(false);
  };

  const transcribeAndSend = async (blob) => {
    setIsTranscribing(true);
    try {
      const form = new FormData();
      form.append('audio', blob, 'voice.webm');
      const r = await fetch(`${API_BASE_URL}/api/tera/transcribe`, {
        method: 'POST',
        body: form,
      });
      const data = await r.json();
      if (!r.ok || !data?.transcript) {
        throw new Error(data?.error || "Couldn't transcribe that.");
      }
      // Auto-send the transcript as the user's next message.
      await send(data.transcript);
    } catch (err) {
      setError(err.message || 'Transcription failed.');
    } finally {
      setIsTranscribing(false);
    }
  };

  const composerDisabled = isStreaming || isRecording || isTranscribing;

  return (
    <>
      {/* Teaser bubble — appears above the FAB on first visit and cycles
          through engagement prompts. Clicking it opens the chat. */}
      {!open && teaserIndex >= 0 && !teaserDismissed && (
        <button
          type="button"
          className="tera-teaser"
          onClick={() => setOpen(true)}
          aria-label="Open Tera chat"
        >
          <span className="tera-teaser-text" key={teaserIndex}>
            {TEASERS[teaserIndex]}
          </span>
          <span
            className="tera-teaser-close"
            role="button"
            tabIndex={0}
            onClick={dismissTeaser}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') dismissTeaser(e); }}
            aria-label="Dismiss"
          >
            <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </span>
        </button>
      )}

      {/* FAB stays visible in both states — it always shows the Tera bot
          image (never an X). Click toggles the panel; the header X inside
          the panel is a second close affordance for users whose mouse is
          already up there. The pulse animation is muted while the panel
          is open so it doesn't distract from the conversation. */}
      <button
        type="button"
        className={`tera-fab ${open ? 'is-open' : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-label={open ? 'Close Tera chat' : "Open Tera — Terview's AI career copilot"}
        title={open ? 'Close chat' : "Hey, I'm Tera — chat with me"}
      >
        <span className="tera-fab-inner">
          <img
            className="tera-fab-img"
            src={TERA_AVATAR}
            alt=""
            aria-hidden="true"
            draggable={false}
          />
          {!open && <span className="tera-fab-pulse" aria-hidden="true" />}
        </span>
      </button>

      {/* Pointer arrow between the chat panel and the Tera FAB — gives the
          panel a "speech tail" so it visually belongs to the bot below. */}
      {open && <div className="tera-panel-pointer" aria-hidden="true" />}

      {open && (
        <div className="tera-panel" role="dialog" aria-label="Tera chat">
          <div className="tera-panel-head">
            <div className="tera-panel-avatar">
              <img src={TERA_AVATAR} alt="" aria-hidden="true" draggable={false} />
            </div>
            <div className="tera-panel-meta">
              <div className="tera-panel-name">Tera</div>
              <div className="tera-panel-status">
                <span className="tera-status-dot" aria-hidden="true" />
                AI Career Copilot — online
              </div>
            </div>
            <button
              type="button"
              className="tera-panel-close"
              onClick={() => setOpen(false)}
              aria-label="Close chat"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>

          <div className="tera-messages">
            {messages.map((m) => (
              <div
                key={m.id}
                className={`tera-msg tera-msg-${m.role} ${m.error ? 'is-error' : ''}`}
              >
                {m.role === 'tera' && (
                  <div className="tera-msg-avatar">
                    <img src={TERA_AVATAR} alt="" aria-hidden="true" draggable={false} />
                  </div>
                )}
                <div className="tera-msg-bubble">
                  {m.text}
                  {m.streaming && <span className="tera-cursor" aria-hidden="true" />}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {error && <div className="tera-error">{error}</div>}

          <div className="tera-composer">
            <textarea
              ref={inputRef}
              className="tera-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={isRecording ? 'Listening…' : isTranscribing ? 'Transcribing…' : 'Ask Tera anything…'}
              rows={1}
              disabled={composerDisabled}
            />
            <button
              type="button"
              className={`tera-mic-btn ${isRecording ? 'is-recording' : ''}`}
              onClick={isRecording ? stopRecording : startRecording}
              disabled={isStreaming || isTranscribing}
              aria-label={isRecording ? 'Stop recording' : 'Record voice message'}
              title={isRecording ? 'Stop' : 'Hold to talk'}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="3" width="6" height="11" rx="3" />
                <path d="M5 11a7 7 0 0 0 14 0" />
                <path d="M12 18v3" />
                <path d="M8 21h8" />
              </svg>
            </button>
            <button
              type="button"
              className="tera-send-btn"
              onClick={() => send()}
              disabled={!input.trim() || composerDisabled}
              aria-label="Send message"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3.5 11.5L20 4l-7.5 16.5L10 13z" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
